import type { IncomeShape } from "./types";

/**
 * Card -> income_shape mapping (tech spec "Question vocabulary ->
 * income_shape mapping"). Fixed table, pinned by
 * `income-shape-vocabulary.test.ts` — no implementing agent guesses. Five
 * cards map onto five of the six `IncomeShape` values; `other` is only ever
 * reached through the not-employed refinement fork (see `types.ts`
 * `NotEmployedRefinement`), never a direct how-you-earn card.
 */
export type HowYouEarnCardId =
  | "salaried"
  | "hourly_commission"
  | "self_employed"
  | "asset_based"
  | "not_working";

export interface HowYouEarnCard {
  description: string;
  id: HowYouEarnCardId;
  incomeShape: IncomeShape;
  title: string;
}

export const HOW_YOU_EARN_CARDS: readonly HowYouEarnCard[] = [
  {
    description: "W-2, same amount each pay period",
    id: "salaried",
    incomeShape: "salaried_w2",
    title: "Salaried — a regular paycheck",
  },
  {
    description:
      "Pay varies — totally normal, we'll use your best annual estimate",
    id: "hourly_commission",
    incomeShape: "hourly_commission_w2",
    title: "Hourly, tips, or commission",
  },
  {
    description: "1099, freelance, or your own company",
    id: "self_employed",
    incomeShape: "self_employed",
    title: "Self-employed or business owner",
  },
  {
    description: "Retired, between roles, or asset-based",
    id: "asset_based",
    incomeShape: "asset_based",
    title: "Mainly living on savings & investments",
  },
  {
    description: "We'll find the right path — no dead-ends",
    id: "not_working",
    incomeShape: "not_employed",
    title: "Not working right now",
  },
] as const;

export interface VerifyPathDescriptor {
  /** Body copy shown inside the live verify-path panel. */
  description: string;
  /** Short method label (e.g. "Bank-verified income"). Situations, never
   * loan-product names (scenario 721 — copy gate). */
  method: string;
  /** Panel heading — what the buyer will be asked to do next. */
  title: string;
}

/**
 * Situation -> verify path, one row per `IncomeShape` value (all six —
 * `other` included so the escape hatch is never a dead branch in the FE
 * panel either, mirroring the backend `PlanFor` router's full coverage).
 * Copy audited against the design-rule gate: no "DSCR", "bank-statement
 * loan", "non-QM", or "Payroll" anywhere.
 */
export const VERIFY_PATH_BY_SHAPE: Record<IncomeShape, VerifyPathDescriptor> = {
  asset_based: {
    description: "We verify your balances and reserves directly.",
    method: "Asset-based · balances verified",
    title: "Connect your savings & investment accounts",
  },
  hourly_commission_w2: {
    description:
      "We read the last 12 months to smooth variable pay into a steady income figure.",
    method: "Bank-verified · 12-mo deposits",
    title: "Connect your main deposit account",
  },
  not_employed: {
    description:
      "We'll capture your assets and point you to the fastest path to ready — no dead-ends.",
    method: "Assets only · interim",
    title: "Connect any accounts you have",
  },
  other: {
    description:
      "We'll work from what you share and keep your path moving — no dead-ends.",
    method: "Self-reported · interim",
    title: "Connect any accounts you have",
  },
  salaried_w2: {
    description:
      "Verifies your income and assets in seconds — the strongest passport you can build.",
    method: "Bank-verified income",
    title: "Connect the account your paycheck lands in",
  },
  self_employed: {
    description:
      "We read 12–24 months of deposits to estimate your income — no tax returns needed.",
    method: "Bank-statement-derived · 12–24 mo deposits",
    title: "Link the account your business income lands in",
  },
};

export function verifyPathFor(shape: IncomeShape): VerifyPathDescriptor {
  return VERIFY_PATH_BY_SHAPE[shape];
}

/** Business-type presets for the self-employed shape-detail screen (tech
 * spec q180 — validated list is open, config-driven, does not block build;
 * this is the build-time default set from the design). */
export const BUSINESS_TYPE_PRESETS: ReadonlyArray<{
  label: string;
  value: "consulting" | "trades" | "ecommerce" | "creative" | "other";
}> = [
  { label: "Consulting", value: "consulting" },
  { label: "Trades", value: "trades" },
  { label: "E-commerce", value: "ecommerce" },
  { label: "Creative", value: "creative" },
  { label: "Other", value: "other" },
];

/** Ownership-percentage presets for the self-employed shape-detail screen. */
export const OWNERSHIP_PCT_PRESETS: readonly number[] = [25, 50, 100];
